import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { FaShirt } from "react-icons/fa6";
import {
    Badge,
    Card,
    Col,
    Nav,
    Row,
    Tab,
    Image,

} from 'react-bootstrap';
import axios from 'axios';
import useGoogleMaps from '../../hooks/useGoogleMaps';
import useUserRoles from '../../hooks/useUserRoles';
import useMyDetails from '../../hooks/useMyDetails';
import SetRosterModal from './SetRosterModal';

const googleAPIKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function GameDetailPage() {
    const { gameId } = useParams();
    const [gameDetails, setGameDetails] = useState(null);

    const [gameLoading, setGameLoading] = useState(true);
    const [gameError, setGameError] = useState(null);
    const { data: currentUser,
        isLoading: userLoading,
        error: userError } = useMyDetails();

    const { isCaptain, isCommissioner } = useUserRoles();
    const [showRosterModal, setShowRosterModal] = useState(false);
    const [selectedTeamPlayers, setSelectedTeamPlayers] = useState([]);
    const [selectedTeamId, setSelectedTeamId] = useState(null);
    const [activeTeamTab, setActiveTeamTab] = useState("home");
    const [relatedEvents, setRelatedEvents] = useState([]);
    const [eventsLoading, setEventsLoading] = useState(false);

    const mapRef = useRef(null);
    const google = useGoogleMaps(googleAPIKey);

    useEffect(() => {
        axios.get(`/api/games/${gameId}/?include_non_attending=true`, {
            withCredentials: true,
        })
            .then((response) => {
                setGameDetails(response.data);
                setGameLoading(false);
            })
            .catch((err) => {
                console.error('Error fetching game details:', err);
                setGameError(err);
                setGameLoading(false);
            });
    }, [gameId]);


    // Fetch related events when game details are loaded
    useEffect(() => {
        if (!gameDetails) return;

        const fetchRelatedEvents = async () => {
            setEventsLoading(true);
            try {
                // Extract the date from the game's scheduled_date
                // The scheduled_date comes as a datetime string, we need to extract just the date part
                let gameDate;

                if (gameDetails.scheduled_date) {
                    // Handle different possible formats
                    if (typeof gameDetails.scheduled_date === 'string') {
                        // If it's already a string, try to parse it
                        const dateObj = new Date(gameDetails.scheduled_date);
                        if (!isNaN(dateObj.getTime())) {
                            gameDate = dateObj.toISOString().split('T')[0]; // Extract YYYY-MM-DD
                        }
                    } else if (gameDetails.scheduled_date instanceof Date) {
                        gameDate = gameDetails.scheduled_date.toISOString().split('T')[0];
                    }
                }

                // Fallback: if we couldn't parse the date, use the formatted date fields
                if (!gameDate && gameDetails.scheduled_date_only) {
                    // scheduled_date_only is in format "MMM DD" like "Jan 15"
                    // We need to construct the full date using the current year
                    const currentYear = new Date().getFullYear();
                    const dateParts = gameDetails.scheduled_date_only.split(' ');
                    if (dateParts.length === 2) {
                        const month = dateParts[0];
                        const day = dateParts[1];
                        const dateString = `${month} ${day}, ${currentYear}`;
                        const fallbackDate = new Date(dateString);
                        if (!isNaN(fallbackDate.getTime())) {
                            gameDate = fallbackDate.toISOString().split('T')[0];
                        }
                    }
                }

                if (!gameDate) {
                    console.error('Could not parse game date from:', gameDetails.scheduled_date);
                    setRelatedEvents([]);
                    return;
                }

                const response = await axios.post(
                    '/api/leagues/events/by_date/',
                    { date: gameDate },
                    { withCredentials: true }
                );

                // Filter out the current game from the events list
                const filteredEvents = response.data.events.filter(
                    event => !event.games || !event.games.some(game => game.id === parseInt(gameId))
                );

                setRelatedEvents(filteredEvents);
            } catch (error) {
                console.error('Error fetching related events:', error);
                setRelatedEvents([]);
            } finally {
                setEventsLoading(false);
            }
        };

        fetchRelatedEvents();
    }, [gameDetails, gameId]);

    // Set active tab based on which team the user is on
    useEffect(() => {
        if (!currentUser || !gameDetails) return;

        // Calculate team membership inside the effect
        const isUserOnHomeTeam = isUserOnTeam(homeParticipant.object_id);
        const isUserOnAwayTeam = isUserOnTeam(awayParticipant.object_id);

        // Check if user is on home team
        if (isUserOnHomeTeam) {
            setActiveTeamTab("home");
        }
        // Check if user is on away team
        else if (isUserOnAwayTeam) {
            setActiveTeamTab("away");
        }
        // If user is not on either team, keep default (home)
    }, [currentUser, gameDetails]);

    useEffect(() => {
        if (!google || !gameDetails) return;


        const { formatted_address: address } = gameDetails;

        /* Geocode the address just once */
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ address }, (results, status) => {
            if (status !== "OK" || !results[0]) return;

            const map = new google.maps.Map(mapRef.current, {
                center: results[0].geometry.location,
                zoom: 14,
                mapId: 'e7e04a9922ec37551a45eaad',
                mapTypeControl: false,
                fullscreenControl: false,
            });


            const { AdvancedMarkerElement } = google.maps.marker;
            new AdvancedMarkerElement({
                map,
                position: results[0].geometry.location,
            });
        });
        return () => {
            if (mapRef.current) mapRef.current.innerHTML = "";
        };
    }, [google, gameDetails]);


    if (userLoading || gameLoading) return <div className="container">Loading team details...</div>;
    if (userError || gameError) return <div className="container text-danger">Failed to load.</div>;
    // if (loading) {
    //     return <div className="container">Loading team details...</div>;
    // }

    // if (error) {
    //     return <div className="container text-danger">{error}</div>;
    // }
    const homeParticipant = gameDetails.participants?.find((p) => p.is_home && p.content_type === 'team') || {};
    const awayParticipant = gameDetails.participants?.find((p) => p.is_away && p.content_type === 'team') || {};

    const homePlayers = homeParticipant.team_players || [];
    const awayPlayers = awayParticipant.team_players || [];

    const isByeGame = !awayParticipant.participant_name || gameDetails.game_type === 'bye';

    const homeAttending = homePlayers.filter(p => p.attending);
    const homeNotAttending = homePlayers.filter(p => !p.attending);
    const awayAttending = awayPlayers.filter(p => p.attending);
    const awayNotAttending = awayPlayers.filter(p => !p.attending);
    // Helper function to check if current user is a captain of the specific team
    const isUserCaptainOfTeam = (teamId) => {
        if (!currentUser || !currentUser.teams || !isCaptain) return false;

        // Check if user has a team with the same ID and is a captain
        return currentUser.teams.some(team => team.id === teamId);
    };

    // Helper function to check if current user is on the specific team
    const isUserOnTeam = (teamId) => {
        if (!currentUser || !currentUser.teams) return false;
        return currentUser.teams.some(team => team.id === teamId);
    };

    // Helper function to check if current user can update a player's attendance
    const canUpdatePlayerAttendance = (player, teamId) => {
        if (!currentUser) return false;

        // Player can update their own attendance
        if (currentUser.players && currentUser.players.some(p => p.id === player.id)) {
            return true;
        }

        // Captain can update any player on their team
        if (isCaptain && isUserCaptainOfTeam(teamId)) {
            return true;
        }

        // Commissioner can update any player
        if (isCommissioner) {
            return true;
        }

        return false;
    };

    // Helper function to handle attendance toggle
    const handlePlayerAttendanceToggle = async (player, teamId, currentAttending) => {
        if (!canUpdatePlayerAttendance(player, teamId)) {
            console.error("User doesn't have permission to update this player's attendance");
            return;
        }

        try {
            const response = await axios.patch(
                `/api/games/${gameId}/players/${player.id}/attending/`,
                { attending: !currentAttending },
                {
                    withCredentials: true,
                }
            );

            if (response.status === 200) {
                // Update local state
                setGameDetails((prevDetails) => {
                    if (!prevDetails) return prevDetails;

                    return {
                        ...prevDetails,
                        participants: prevDetails.participants.map((participant) => {
                            if (participant.content_type === 'team' && participant.object_id === teamId) {
                                return {
                                    ...participant,
                                    team_players: participant.team_players.map((p) =>
                                        p.id === player.id ? { ...p, attending: !currentAttending } : p
                                    ),
                                };
                            }
                            return participant;
                        }),
                    };
                });
            }
        } catch (error) {
            console.error("Error updating player attendance:", error);
        }
    };

    // Check if user is captain of home team
    const isHomeTeamCaptain = isUserCaptainOfTeam(homeParticipant.object_id);
    // Check if user is captain of away team  
    const isAwayTeamCaptain = isUserCaptainOfTeam(awayParticipant.object_id);

    // Check if user is on home or away team
    const isUserOnHomeTeam = isUserOnTeam(homeParticipant.object_id);
    const isUserOnAwayTeam = isUserOnTeam(awayParticipant.object_id);

    // Determine team order for mobile: user's team first if they're on one, otherwise home first
    const getTeamOrder = () => {
        // Always return home team first, away team second for desktop
        // The ordering will be handled by Bootstrap classes for mobile
        return {
            firstTeam: {
                participant: homeParticipant,
                players: homePlayers,
                isCaptain: isHomeTeamCaptain,
                isUserTeam: isUserOnHomeTeam
            },
            secondTeam: {
                participant: awayParticipant,
                players: awayPlayers,
                isCaptain: isAwayTeamCaptain,
                isUserTeam: isUserOnAwayTeam
            }
        };
    };

    const teamOrder = getTeamOrder();

    // Separate attending and non-attending players for each team
    const getTeamPlayersByAttendance = (players) => {
        const attending = players.filter(player => player.attending);
        const nonAttending = players.filter(player => !player.attending);
        return { attending, nonAttending };
    };

    const firstTeamPlayers = getTeamPlayersByAttendance(teamOrder.firstTeam.players);
    const secondTeamPlayers = getTeamPlayersByAttendance(teamOrder.secondTeam.players);

    // Determine mobile ordering classes
    const getMobileOrderClasses = () => {
        if (isUserOnAwayTeam && !isUserOnHomeTeam) {
            // User is only on away team, show away first on mobile
            return {
                firstTeamClass: "order-2 order-sm-1", // Away team (second) shows first on mobile
                secondTeamClass: "order-1 order-sm-2"  // Home team (first) shows second on mobile
            };
        } else {
            // User is on home team or not on either team, show home first on mobile
            return {
                firstTeamClass: "order-1 order-sm-1", // Home team shows first on both
                secondTeamClass: "order-2 order-sm-2"  // Away team shows second on both
            };
        }
    };

    const mobileOrderClasses = getMobileOrderClasses();

    const handleOpenRosterModal = (teamPlayers, teamId) => {
        setSelectedTeamPlayers(teamPlayers);
        setSelectedTeamId(teamId);
        setShowRosterModal(true);
    };

    const handleSaveRoster = async (rosterOrder) => {
        if (!selectedTeamId || !gameId) {
            console.error('Missing teamId or gameId for roster save');
            return;
        }

        try {
            const response = await axios.post(
                `/api/games/${gameId}/teams/${selectedTeamId}/roster/`,
                {
                    ordered_player_ids: rosterOrder.map(player => player.id)
                },
                {
                    withCredentials: true,
                }
            );

            console.log('Roster saved successfully:', response.data);

            // Close the modal
            setShowRosterModal(false);

            // Refresh the game details to show the updated roster order
            const gameResponse = await axios.get(`/api/games/${gameId}/?include_non_attending=true`, {
                withCredentials: true,
            });
            setGameDetails(gameResponse.data);

        } catch (error) {
            console.error('Error saving roster:', error);
            // You could add error handling here, like showing a toast notification
        }
    };

    return (
        <>
            {/* Card START */}
            <Card className="p-0">
                <div className="banner-img position-relative" style={{ backgroundColor: '#f8f9fa' }}>
                    <img
                        src={`/static/images/kb_static/lemont_rec_league_banner.png`}
                        alt="League Banner"
                        className="w-100 h-auto rounded-top"
                        style={{ maxHeight: '200px', objectFit: 'contain' }}
                    />
                    {isByeGame ? (
                        <div className="position-absolute top-0 end-0 p-3">
                            <div className="bg-mode text-center rounded overflow-hidden p-1 d-inline-block shadow">
                                <div className="bg-primary p-2 text-white rounded-top lh-1">{gameDetails.scheduled_day_of_week}</div>
                                <div className="mb-0 py-2 lh-1 fw-bold">{gameDetails.scheduled_date_only}</div>
                                <p className="small mb-0">{gameDetails.scheduled_time_only}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="game-date-overlay">
                            <div className="bg-mode text-center rounded overflow-hidden p-1 d-inline-block shadow">
                                <div className="bg-primary p-2 text-white rounded-top lh-1">{gameDetails.scheduled_day_of_week}</div>
                                <div className="mb-0 py-2 lh-1 fw-bold">{gameDetails.scheduled_date_only}</div>
                                <p className="small mb-0">{gameDetails.scheduled_time_only}</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Card body START */}
                <Card.Body className="py-0">
                    <Row className="mb-3">
                        <Col className="text-center d-flex flex-column justify-content-start align-items-center">
                            <div className="avatar avatar-xxl mt-n3 mb-3">
                                <Image
                                    src={homeParticipant.participant_logo}
                                    alt={`${homeParticipant.participant_name} logo`}
                                    className="avatar-img"
                                    roundedCircle
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        backgroundColor: "white",
                                        border: '3px solid white'
                                    }}
                                />
                            </div>
                            <h2 className="team-name">{homeParticipant.participant_name}</h2>
                            <p className="text-uppercase text-muted fw-semibold letter-spacing-1 mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.1em' }}>Home</p>
                            {homeParticipant.score !== null && (
                                <div className="mt-2">
                                    <Badge
                                        bg={homeParticipant.is_winner ? "success" : "secondary"}
                                        className="fs-4 px-3 py-2"
                                    >
                                        {homeParticipant.score}
                                    </Badge>
                                    {homeParticipant.is_winner && (
                                        <div className="mt-1">
                                            <i className="bi bi-trophy-fill text-warning"></i>
                                            <span className="ms-1 small">Winner</span>
                                        </div>
                                    )}
                                </div>
                            )}
                            {isByeGame && (
                                <div className="mt-3">
                                    <Badge bg="secondary" className="fs-5 px-4 py-2">
                                        <i className="bi bi-calendar-x me-2"></i>
                                        BYE WEEK
                                    </Badge>
                                </div>
                            )}
                        </Col>
                        {!isByeGame && (
                            <>
                                <Col xs={2} className="d-flex flex-column justify-content-center align-items-center">
                                    <h2 className="mb-0 fw-light text-muted fs-1">VS</h2>
                                </Col>
                                <Col className="text-center d-flex flex-column justify-content-start align-items-center">
                                    <div className="avatar avatar-xxl mt-n3 mb-3">
                                        <Image
                                            src={awayParticipant.participant_logo}
                                            alt={`${awayParticipant.participant_name} logo`}
                                            className="avatar-img"
                                            roundedCircle
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                backgroundColor: "white",
                                                border: '3px solid white'
                                            }}
                                        />
                                    </div>
                                    <h2 className="team-name">{awayParticipant.participant_name}</h2>
                                    <p className="text-uppercase text-muted fw-semibold letter-spacing-1 mb-2" style={{ fontSize: '0.75rem', letterSpacing: '0.1em' }}>Away</p>
                                    {awayParticipant.score !== null && (
                                        <div className="mt-2">
                                            <Badge
                                                bg={awayParticipant.is_winner ? "success" : "secondary"}
                                                className="fs-4 px-3 py-2"
                                            >
                                                {awayParticipant.score}
                                            </Badge>
                                            {awayParticipant.is_winner && (
                                                <div className="mt-1">
                                                    <i className="bi bi-trophy-fill text-warning"></i>
                                                    <span className="ms-1 small">Winner</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Col>
                            </>
                        )}
                    </Row>
                </Card.Body>
                {/* Card body END */}
            </Card >
            {/* Card END */}

            <div className="row mt-3 mb-2" >
                {/* Events Section */}
                <Col xs={12} sm={6} className="mb-3">
                    <Card className="h-100">
                        <div className="card-header pb-0 border-0">
                            <h5 className="card-title mb-0">Game Events</h5>
                        </div>
                        <div className="card-body">
                            {eventsLoading ? (
                                <div className="text-center py-3">
                                    <div className="spinner-border spinner-border-sm" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <span className="ms-2">Loading events...</span>
                                </div>
                            ) : relatedEvents.length > 0 ? (
                                <div className="list-group list-group-flush">
                                    {relatedEvents.map((event) => (
                                        <div key={event.id} className="list-group-item border-0 px-0">
                                            <div className="d-flex align-items-center">
                                                <div className="flex-grow-1 ms-3">
                                                    {event.event_type && (
                                                        <span className="badge bg-light text-dark small mb-1">
                                                            {event.event_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                        </span>
                                                    )}
                                                    <h6 className="mb-1">{event.location_name}</h6>
                                                    <p className="small text-muted mb-0">
                                                        <i className="bi bi-clock me-1"></i>
                                                        {new Date(event.datetime).toLocaleTimeString([], {
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </p>
                                                    {event.address_line1 && (
                                                        <p className="small text-muted mb-0">
                                                            <i className="bi bi-geo-alt me-1"></i>
                                                            {event.address_line1} {event.address_line2} {event.city}, {event.state} {event.zip_code}
                                                        </p>
                                                    )}

                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-3 text-muted">
                                    <i className="bi bi-calendar-x fs-4 mb-2"></i>
                                    <p className="mb-0">No other events on this date</p>
                                </div>
                            )}
                        </div>
                    </Card>
                </Col>
                <Col xs={12} sm={6} className="mb-3">
                    <Card className="h-100">
                        <div className="card-header pb-0 border-0">
                            <div className="row">
                                <div className="col d-flex align-items-center">
                                    <h5 className="card-title mb-0">Game Details</h5>
                                    <div className="small ms-3">
                                        <i className="bi bi-calendar pe-1"></i>
                                        {gameDetails.scheduled_date_only} @ {gameDetails.scheduled_time_only}
                                    </div>
                                </div>
                            </div>
                            <p className="small">
                                <i className="bi bi-geo-alt pe-1"></i>
                                {gameDetails.location.street_1} {gameDetails.location.street_2} {gameDetails.location.city}, {gameDetails.location.state}
                            </p>
                        </div>
                        <div className="card-body pt-1 mb-2">
                            <div className="row">
                                <div
                                    ref={mapRef}
                                    style={{ width: "100%", height: 280, borderRadius: 4 }}
                                    className="grayscale"
                                />
                            </div>
                        </div>
                    </Card>
                </Col>
            </div >

            {/* Teams Section with Tabs */}
            < div className="card" >
                <div className="card-header d-sm-flex align-items-center text-center justify-content-sm-between border-0 pb-0">
                    <h1 className="h4 card-title">Teams</h1>
                </div>

                <div className="card-body">
                    <Tab.Container activeKey={activeTeamTab} onSelect={(k) => setActiveTeamTab(k)}>
                        <Nav variant="tabs" className="nav-bottom-line justify-content-center justify-content-md-start">
                            <Nav.Item>
                                <Nav.Link eventKey="home">
                                    {homeParticipant.participant_name} (Home)
                                </Nav.Link>
                            </Nav.Item>
                            {!isByeGame && (
                                <Nav.Item>
                                    <Nav.Link eventKey="away">
                                        {awayParticipant.participant_name} (Away)
                                    </Nav.Link>
                                </Nav.Item>
                            )}
                        </Nav>

                        <Tab.Content className="tab-content mb-0 pb-0">
                            {/* Home Team Tab */}
                            <Tab.Pane eventKey="home" className="tab-pane fade">
                                <div>
                                    <div className="card-header border-0">
                                        <div className="row g-2">
                                            <div className="col-12 col-sm-6 text-center text-sm-start">
                                                <h5 className="card-title mb-1">Team Captain</h5>
                                                <p className="small mb-0 text-truncate"> {homeParticipant.team_captain || 'No Captain'} </p>
                                            </div>
                                            <div className="col-12 col-sm-6 text-center text-sm-end">
                                                <h5 className="card-title mb-1">Team Color</h5>
                                                <p className="small mb-0 d-flex align-items-center justify-content-center justify-content-sm-end gap-2">
                                                    <FaShirt style={{ fontSize: '20px', color: homeParticipant.team_color?.value }}></FaShirt>
                                                    <span className="text-truncate d-inline-block" style={{ maxWidth: '120px' }}>{homeParticipant.team_color?.name}</span>
                                                </p>
                                            </div>
                                        </div>
                                        <div className="row mt-2">
                                            {isUserOnHomeTeam ? (
                                                <>
                                                    <div className="col-6 text-center">
                                                        <span className="badge bg-primary bg-opacity-75 small d-inline">
                                                            <i className="bi bi-person-check me-1"></i>
                                                            Your Team
                                                        </span>
                                                    </div>
                                                    <div className="col-6 text-center">
                                                        <h6 className="text-muted mb-0 fw-normal">
                                                            <i className="bi bi-people me-1"></i>
                                                            {homeParticipant.participant_name}
                                                        </h6>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="col-12 text-center">
                                                    <h6 className="text-muted mb-0 fw-normal">
                                                        <i className="bi bi-people me-1"></i>
                                                        {homeParticipant.participant_name}
                                                    </h6>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="container p-3 d-flex align-items-center justify-content-between">
                                        <div className="col-sm-4 d-flex align-items-center justify-content-center">
                                            <div className="d-flex">
                                                <i className="bi bi-trophy fs-4"></i>
                                                <div className="ms-3">
                                                    <h5 className="mb-0">{homeParticipant.team_wins}</h5>
                                                    <p className="mb-0">Wins</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-sm-4 d-flex align-items-center justify-content-center">
                                            <div className="d-flex">
                                                <i className="bi bi-trophy-fill fs-4"></i>
                                                <div className="ms-3">
                                                    <h5 className="mb-0">{homeParticipant.team_losses}</h5>
                                                    <p className="mb-0">Losses</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-sm-4 d-flex align-items-center justify-content-center">
                                            <div className="d-flex">
                                                <i className="bi bi-trophy-fill fs-4"></i>
                                                <div className="ms-3">
                                                    <h5 className="mb-0">{homeParticipant.team_draws}</h5>
                                                    <p className="mb-0">Draws</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-header border-0">
                                        <div className="row g-2 align-items-center">
                                            <div className="col-6 col-sm-auto">
                                                <div className="d-flex align-items-center justify-content-start justify-content-sm-start w-100">
                                                    <h5 className="card-title mb-0 d-flex align-items-center gap-2">
                                                        <span className="badge bg-success bg-opacity-10 text-success border border-success-subtle small" style={{ fontSize: '0.8rem' }}>
                                                            <i className="bi bi-person-check me-1"></i>
                                                            {homePlayers.filter(p => p.attending).length}
                                                        </span>
                                                        <span>Players</span>
                                                    </h5>
                                                </div>
                                            </div>
                                            <div className="col-6 ms-sm-auto">
                                                {(isHomeTeamCaptain || isCommissioner) && (
                                                    <div className="d-flex align-items-end justify-content-end gap-2">
                                                        <button
                                                            className="btn btn-primary btn-sm"
                                                            onClick={() => handleOpenRosterModal(homePlayers, homeParticipant.object_id)}
                                                            disabled={homeParticipant.roster_is_final && !isCommissioner}
                                                        >
                                                            Set Roster
                                                        </button>
                                                        {homeParticipant.roster_is_final && (
                                                            <span className="badge bg-success">
                                                                <i className="bi bi-lock-fill me-1"></i>
                                                                Final
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {/* Pending Approval Badge */}
                                        {homeParticipant.roster_pending_approval && (
                                            <div className="row mt-2">
                                                <div className="col-12 text-center">
                                                    <span className="badge bg-warning text-dark">
                                                        <i className="bi bi-clock me-1"></i>
                                                        Pending Approval
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="card-body pt-0">
                                        <div className="row">
                                            {homeAttending.map((player) => (
                                                <div key={player.id} className="col-6 col-sm-12 d-flex flex-wrap align-items-center mb-3">
                                                    <div className="col-6 col-sm-12 avatar avatar-md">
                                                        <img
                                                            className="avatar-img rounded-circle"
                                                            src={
                                                                player.profile_picture ||
                                                                `/static/images/kb_static/lemont_rec_leagues_circle.png`
                                                            }
                                                            alt={`${player.first_name} ${player.last_name}`}
                                                        />
                                                    </div>
                                                    <div className="col-8 col-sm-6 ms-sm-2 my-2 my-sm-0">
                                                        <h6 className="mb-0">{player.first_name} {player.last_name}</h6>
                                                        <p className="small mb-0"> <i className="bi bi-geo-alt pe-1"></i>Lemont </p>
                                                    </div>
                                                    <div className="col-6 col-sm-4 text-end ms-sm-auto mt-2 mt-sm-0">
                                                        <input
                                                            type="checkbox"
                                                            className="btn-check"
                                                            id={`Interested-home-${player.id}`}
                                                            checked={player.attending}
                                                            disabled={!canUpdatePlayerAttendance(player, homeParticipant.object_id)}
                                                            onChange={(e) => handlePlayerAttendanceToggle(player, homeParticipant.object_id, player.attending)}
                                                        />
                                                        <label
                                                            className={`btn btn-sm ${player.attending ? 'btn-success' : 'btn-outline-success'} ${!canUpdatePlayerAttendance(player, homeParticipant.object_id) ? 'opacity-50' : ''}`}
                                                            htmlFor={`Interested-home-${player.id}`}
                                                        >
                                                            <i className="fa-solid fa-thumbs-up me-1"></i> Attending
                                                        </label>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Non-Attending Players Section */}
                                        {homeNotAttending.length > 0 && (
                                            <>
                                                <hr className="my-3" />
                                                <h6 className="text-muted mb-3">
                                                    <i className="bi bi-x-circle me-2"></i>
                                                    Not Attending ({homeNotAttending.length})
                                                </h6>
                                                <div className="row">
                                                    {homeNotAttending.filter(p => !p.attending).map((player) => (
                                                        <div key={player.id} className="col-6 col-sm-12 d-flex flex-wrap align-items-center mb-3">
                                                            <div className="col-6 col-sm-12 avatar avatar-md">
                                                                <img
                                                                    className="avatar-img rounded-circle opacity-50"
                                                                    src={
                                                                        player.profile_picture ||
                                                                        `/static/images/kb_static/lemont_rec_leagues_circle.png`
                                                                    }
                                                                    alt={`${player.first_name} ${player.last_name}`}
                                                                />
                                                            </div>
                                                            <div className="col-8 col-sm-6 ms-sm-2 my-2 my-sm-0">
                                                                <h6 className="mb-0 text-muted">{player.first_name} {player.last_name}</h6>
                                                                <p className="small mb-0 text-muted"> <i className="bi bi-geo-alt pe-1"></i>Lemont </p>
                                                            </div>
                                                            <div className="col-6 col-sm-4 text-end ms-sm-auto mt-2 mt-sm-0">
                                                                <input
                                                                    type="checkbox"
                                                                    className="btn-check"
                                                                    id={`NotInterested-home-${player.id}`}
                                                                    checked={!player.attending}
                                                                    disabled={!canUpdatePlayerAttendance(player, homeParticipant.object_id)}
                                                                    onChange={(e) => handlePlayerAttendanceToggle(player, homeParticipant.object_id, player.attending)}
                                                                />
                                                                <label
                                                                    className={`btn btn-sm ${canUpdatePlayerAttendance(player, homeParticipant.object_id) ? 'btn-outline-danger' : 'btn-outline-danger'}`}
                                                                    htmlFor={`NotInterested-home-${player.id}`}
                                                                >
                                                                    <i className="fa-solid fa-times me-1"></i> Not Attending
                                                                </label>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </Tab.Pane>
                            {!isByeGame && (
                                <>
                                    {/* Away Team Tab */}
                                    <Tab.Pane eventKey="away" className="tab-pane fade">
                                        <div>
                                            <div className="card-header border-0">
                                                <div className="row g-2">
                                                    <div className="col-12 col-sm-6 text-center text-sm-start">
                                                        <h5 className="card-title mb-1">Team Captain</h5>
                                                        <p className="small mb-0 text-truncate"> {awayParticipant.team_captain || 'No Captain'} </p>
                                                    </div>
                                                    <div className="col-12 col-sm-6 text-center text-sm-end">
                                                        <h5 className="card-title mb-1">Team Color</h5>
                                                        <p className="small mb-0 d-flex align-items-center justify-content-center justify-content-sm-end gap-2">
                                                            <FaShirt style={{ fontSize: '20px', color: awayParticipant.team_color?.value }}></FaShirt>
                                                            <span className="text-truncate d-inline-block" style={{ maxWidth: '120px' }}>{awayParticipant.team_color?.name}</span>
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="row mt-2">
                                                    {isUserOnAwayTeam ? (
                                                        <>
                                                            <div className="col-6 text-center">
                                                                <span className="badge bg-primary bg-opacity-75 small d-inline">
                                                                    <i className="bi bi-person-check me-1"></i>
                                                                    Your Team
                                                                </span>
                                                            </div>
                                                            <div className="col-6 text-center">
                                                                <h6 className="text-muted mb-0 fw-normal">
                                                                    <i className="bi bi-people me-1"></i>
                                                                    {awayParticipant.participant_name}
                                                                </h6>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="col-12 text-center">
                                                            <h6 className="text-muted mb-0 fw-normal">
                                                                <i className="bi bi-people me-1"></i>
                                                                {awayParticipant.participant_name}
                                                            </h6>
                                                        </div>
                                                    )}

                                                </div>
                                            </div>
                                            <div className="container p-3 d-flex align-items-center justify-content-between">
                                                <div className="col-sm-4 d-flex align-items-center justify-content-center">
                                                    <div className="d-flex">
                                                        <i className="bi bi-trophy fs-4"></i>
                                                        <div className="ms-3">
                                                            <h5 className="mb-0">{awayParticipant.team_wins}</h5>
                                                            <p className="mb-0">Wins</p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-sm-4 d-flex align-items-center justify-content-center">
                                                    <div className="d-flex">
                                                        <i className="bi bi-trophy-fill fs-4"></i>
                                                        <div className="ms-3">
                                                            <h5 className="mb-0">{awayParticipant.team_losses}</h5>
                                                            <p className="mb-0">Losses</p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-sm-4 d-flex align-items-center justify-content-center">
                                                    <div className="d-flex">
                                                        <i className="bi bi-trophy-fill fs-4"></i>
                                                        <div className="ms-3">
                                                            <h5 className="mb-0">{awayParticipant.team_draws}</h5>
                                                            <p className="mb-0">Draws</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="card-header border-0">
                                                <div className="row g-2 align-items-center">
                                                    <div className="col-6 col-sm-auto">
                                                        <div className="d-flex align-items-center justify-content-start justify-content-sm-start w-100">
                                                            <h5 className="card-title mb-0 d-flex align-items-center gap-2">
                                                                <span className="badge bg-success bg-opacity-10 text-success border border-success-subtle small" style={{ fontSize: '0.8rem' }}>
                                                                    <i className="bi bi-person-check me-1"></i>
                                                                    {awayPlayers.filter(p => p.attending).length}
                                                                </span>
                                                                <span>Players</span>
                                                            </h5>
                                                        </div>
                                                    </div>
                                                    <div className="col-6 ms-sm-auto">
                                                        {(isAwayTeamCaptain || isCommissioner) && (
                                                            <div className="d-flex align-items-end justify-content-end gap-2">
                                                                <button
                                                                    className="btn btn-primary btn-sm"
                                                                    onClick={() => handleOpenRosterModal(awayPlayers, awayParticipant.object_id)}
                                                                    disabled={awayParticipant.roster_is_final && !isCommissioner}
                                                                >
                                                                    Set Roster
                                                                </button>
                                                                {awayParticipant.roster_is_final && (
                                                                    <span className="badge bg-success">
                                                                        <i className="bi bi-lock-fill me-1"></i>
                                                                        Final
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                {/* Pending Approval Badge */}
                                                {awayParticipant.roster_pending_approval && (
                                                    <div className="row mt-2">
                                                        <div className="col-12 text-center">
                                                            <span className="badge bg-warning text-dark">
                                                                <i className="bi bi-clock me-1"></i>
                                                                Pending Approval
                                                            </span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="card-body pt-0">
                                                <div className="row">
                                                    {awayAttending.map((player) => (
                                                        <div key={player.id} className="col-6 col-sm-12 d-flex flex-wrap align-items-center mb-3">
                                                            <div className="col-6 col-sm-12 avatar avatar-md">
                                                                <img className="avatar-img rounded-circle"
                                                                    src={
                                                                        player.profile_picture ||
                                                                        `/static/images/kb_static/lemont_rec_leagues_circle.png`
                                                                    }
                                                                    alt={`${player.first_name} ${player.last_name}`}
                                                                />
                                                            </div>
                                                            <div className="col-8 col-sm-6 ms-sm-2 my-2 my-sm-0">
                                                                <h6 className="mb-0">{player.first_name} {player.last_name}</h6>
                                                                <p className="small mb-0"> <i className="bi bi-geo-alt pe-1"></i>Lemont </p>
                                                            </div>
                                                            <div className="col-6 col-sm-4 text-end ms-sm-auto mt-2 mt-sm-0">
                                                                <input
                                                                    type="checkbox"
                                                                    className="btn-check"
                                                                    id={`Interested-away-${player.id}`}
                                                                    checked={player.attending}
                                                                    disabled={!canUpdatePlayerAttendance(player, awayParticipant.object_id)}
                                                                    onChange={(e) => handlePlayerAttendanceToggle(player, awayParticipant.object_id, player.attending)}
                                                                />
                                                                <label
                                                                    className={`btn btn-sm ${player.attending ? 'btn-success' : 'btn-outline-success'} ${!canUpdatePlayerAttendance(player, awayParticipant.object_id) ? 'opacity-50' : ''}`}
                                                                    htmlFor={`Interested-away-${player.id}`}
                                                                >
                                                                    <i className="fa-solid fa-thumbs-up me-1"></i> Attending
                                                                </label>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Non-Attending Players Section */}
                                                {awayNotAttending.length > 0 && (
                                                    <>
                                                        <hr className="my-3" />
                                                        <h6 className="text-muted mb-3">
                                                            <i className="bi bi-x-circle me-2"></i>
                                                            Not Attending ({awayNotAttending.length})
                                                        </h6>
                                                        <div className="row">
                                                            {awayNotAttending.filter(p => !p.attending).map((player) => (
                                                                <div key={player.id} className="col-6 col-sm-12 d-flex flex-wrap align-items-center mb-3">
                                                                    <div className="col-6 col-sm-12 avatar avatar-md">
                                                                        <img
                                                                            className="avatar-img rounded-circle opacity-50"
                                                                            src={
                                                                                player.profile_picture ||
                                                                                `/static/images/kb_static/lemont_rec_leagues_circle.png`
                                                                            }
                                                                            alt={`${player.first_name} ${player.last_name}`}
                                                                        />
                                                                    </div>
                                                                    <div className="col-8 col-sm-6 ms-sm-2 my-2 my-sm-0">
                                                                        <h6 className="mb-0 text-muted">{player.first_name} {player.last_name}</h6>
                                                                        <p className="small mb-0 text-muted"> <i className="bi bi-geo-alt pe-1"></i>Lemont </p>
                                                                    </div>
                                                                    <div className="col-6 col-sm-4 text-end ms-sm-auto mt-2 mt-sm-0">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="btn-check"
                                                                            id={`NotInterested-away-${player.id}`}
                                                                            checked={!player.attending}
                                                                            disabled={!canUpdatePlayerAttendance(player, awayParticipant.object_id)}
                                                                            onChange={(e) => handlePlayerAttendanceToggle(player, awayParticipant.object_id, player.attending)}
                                                                        />
                                                                        <label
                                                                            className={`btn btn-sm ${canUpdatePlayerAttendance(player, awayParticipant.object_id) ? 'btn-outline-danger' : 'btn-outline-danger'}`}
                                                                            htmlFor={`NotInterested-away-${player.id}`}
                                                                        >
                                                                            <i className="fa-solid fa-times me-1"></i> Not Attending
                                                                        </label>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </Tab.Pane>
                                </>
                            )}
                        </Tab.Content>
                    </Tab.Container>
                </div>
            </div >

            {/* Set Roster Modal */}
            < SetRosterModal
                show={showRosterModal}
                onHide={() => setShowRosterModal(false)
                }
                teamPlayers={selectedTeamPlayers}
                onSave={handleSaveRoster}
                gameId={gameId}
                teamId={selectedTeamId}
                isCommissioner={isCommissioner}
            />
        </>
    );
}

export default GameDetailPage;